/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { detectGPUCapability } from "../../../engine/device";

// ---------------------------------------------------------------------------
//  Deterministic test mode hooks
// ---------------------------------------------------------------------------
// When the test runner exports DCW_TEST_MODE=1 (or sets global flag), we lock
// the simulated base FPS so vitest/JSDOM tests compute the correct target
// frame interval without relying on runtime heuristics. This change is
// completely inert in production builds because the env var remains unset.
if (typeof window !== 'undefined') {
  const isTestMode = import.meta?.env?.DCW_TEST_MODE === '1' || globalThis.DCW_TEST_MODE;
  if (isTestMode) {
    window.__DCW_FPS_BASE = 30; // Tests expect this exact baseline
  }
}

import {
  isCaptureMode,
  resolvePixelRatio,
  prefersReducedMotion,
  DEFAULT_PHOSPHOR_PERSISTENCE_MS,
  persistenceDecay,
} from "./crtShaderUtils.js";

/**
 * ============================================================================
 *  DOPE CHAIN WARS – CANONICAL CRT SHADER (v1.0) + MATHEMATICAL TEXT
 * ---------------------------------------------------------------------------
 *  This file implements the canonical CRT renderer with integrated mathematical
 *  text rendering for crisp DOS text display. ASCII art sprites remain
 *  unchanged as they already work perfectly.
 *
 *  Key features:
 *  • Mathematical text rendering directly in shader for crisp text
 *  • Sprite-based ASCII art rendering (unchanged)
 *  • Hybrid approach: best of both worlds
 *
 *  Screenshot capture:
 *  • Sets window.DCW_CRT_READY when WebGL initializes
 *  • Captures framebuffer via readPixels() → window.DCW_SCREENSHOT_PNG
 *  • 2D canvas fallback when WebGL fails (headless environments)
 *  • See docs/adr/002-crt-shader-architecture.md for full documentation
 * ============================================================================
 */

/**
 * CRTShader: Canonical 3D CRT with mathematical text rendering
 * - Physically accurate CRT, bezel, glass, and screen (XdtfzX reference)
 * - DopeOS buffer as iChannel0 for ASCII art
 * - Mathematical text rendering for crisp DOS text
 */

// Profile shape reference for JSDoc
/** @typedef {{ frame:{w:number,h:number}, buffer:{w:number,h:number}, phosphor:{left:number,right:number,top:number,bottom:number} }} CRTProfile */



// Canonical shader parameter defaults 
const DEFAULTS = {
  // Geometry parameters - these should be connected to shader for visual control
  CURVE: 3.0,
  WIDTH: 0.48,
  HEIGHT: 0.3,
  BEZEL_R: 0.05,
  SMOOTH: 0.0042,
  // Glass and reflection effects
  GLASS_TINT: 0.42,
  REFLECTION_BLUR: 0.43,
  BEZEL_REFLECTION_INTENSITY: 0.1337, // Controls the intensity of bezel inner reflections
  // Phosphor color 
  PHOSPHOR_R: 0.2,
  PHOSPHOR_G: 0.9,
  PHOSPHOR_B: 0.2,
  // Bezel and table colors 
  BEZEL_COLOR: [0.8, 0.8, 0.6],
  TABLE_COLOR: [1, 1, 1],
  // Lighting and effects 
  GAMMA: 1.1337,
  AMBIENT: 0.1337,
  VIGNETTE_STRENGTH: 0.1337,
  SHADOW_STRENGTH: 0.1337,
  NOISE_AMOUNT: 0.13,
  REFLECTION_INTENSITY: 0.1337,
  SHINE: 0.13327,
  // Subtle horizontal scanline multiplier (0-0.3 is typical CRT-like)
  SCANLINE_INTENSITY: 1.69,  // REVERTED: Restored original value to preserve visual experience
};

// Default fclback (will be overwritten by profile prop)
const DEFAULT_CRT_WIDTH = 960;
const DEFAULT_CRT_HEIGHT = 720;

/**
 * @param {{dopeOSCanvasRef: React.RefObject, profile: CRTProfile}} props 
 */
const CRTShader = ({ dopeOSCanvasRef, profile }) => {
  const FRAME_W = profile?.frame?.w || DEFAULT_CRT_WIDTH;
  const FRAME_H = profile?.frame?.h || DEFAULT_CRT_HEIGHT;
  const PHOS = profile?.phosphor || { left: 0.158, right: 0.842, top: 0.208, bottom: 0.793 };
  const mountRef = useRef();
  
  // CRITICAL FIX: Track profile ID to prevent unnecessary remounts
  // Only trigger useEffect when profile ID actually changes, not on object reference change
  const profileId = profile?.id || profile?.name || 'default';
  
  // CRITICAL FIX: Stable viewport tracking for Android keyboard handling
  // We use a ref to store the "stable" viewport dimensions (without keyboard)
  // so we can force the CRT to remain full-height when the keyboard squashes window.innerHeight
  const stableViewport = useRef({ w: 0, h: 0 });
  
  const profileBufferW = profile?.buffer?.w || 640;
  const profileBufferH = profile?.buffer?.h || 400;
  const profileContentScale = profile?.contentScale || 0.5;

  // Expose DopeOS canvas to chat system via global function
  useEffect(() => {
    if (dopeOSCanvasRef?.current?.getCanvas) {
      const canvas = dopeOSCanvasRef.current.getCanvas();
      window.DCW_getDopeOSCanvas = () => canvas;
    }
  }, [dopeOSCanvasRef]);

  // Determine GPU capability once (medium/high/low) – kept for backward-compat logs
  const gpuCapability = (typeof window !== 'undefined' && typeof detectGPUCapability === 'function')
    ? detectGPUCapability()
    : 'medium';

  // Detect GPU capability and allow overrides via env or query param
  // Canonical defaults – no automatic shader levels.
  const urlParams = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : new URLSearchParams();
  if (urlParams.has('shader')) console.warn('[CRT] shader level param ignored – canonical settings enforced');

  const optimizedDefaults = { ...DEFAULTS };

  // Fixed params reference (no UI mutations). Allows per-profile overrides so
  // specific views (e.g. icon generation) can tweak CURVE, BEZEL_R, etc.
  const mergedParams = { ...optimizedDefaults, ...(profile?.shaderOverrides || {}) };
  const paramsRef = useRef(mergedParams);

  // Keep paramsRef in sync if profile or its overrides change (e.g., on hot reload)
  // CRITICAL FIX: Use profileId to prevent remount on object reference change
  React.useEffect(() => {
    // Always apply GPU capability optimizations
    const optimized = {
      ...optimizedDefaults,
      ...(profile?.shaderOverrides || {})
    };
    paramsRef.current = optimized;
  }, [profileId, gpuCapability, profile?.shaderOverrides]);

  // CRITICAL FIX: Define isInIframe at component scope so it's available everywhere
  const isInIframe = window.self !== window.top;

  // Three.js refs
  const uniformsRef = useRef();
  useEffect(() => {
    // OPTIMIZATION: Dirty rectangle tracking system for efficient texture updates
    // Tracks changed regions to avoid full canvas redraws when only small areas change
    // Defined here to be accessible in animate() scope
    const dirtyRectTracker = {
      regions: [],
      fullRedraw: true, // Force full redraw on first frame
      lastContentHash: null,
      framesSinceChange: 0,
      
      trackChange(x, y, w, h) {
        this.regions.push({x, y, w, h});
        this.framesSinceChange = 0;
        this.mergeRegions();
      },
      
      mergeRegions() {
        if (this.regions.length < 2) return;
        
        const merged = [];
        const threshold = 25; // pixels - merge if regions are close
        
        for (const region of this.regions) {
          let overlaps = false;
          for (const existing of merged) {
            if (this.overlaps(region, existing, threshold)) {
              existing.x = Math.min(existing.x, region.x);
              existing.y = Math.min(existing.y, region.y);
              existing.w = Math.max(existing.w, region.x + region.w - existing.x);
              existing.h = Math.max(existing.h, region.y + region.h - existing.y);
              overlaps = true;
              break;
            }
          }
          if (!overlaps) merged.push({...region});
        }
        
        this.regions = merged;
      },
      
      overlaps(a, b, threshold) {
        return !(a.x > b.x + b.w + threshold ||
                 a.x + a.w < b.x - threshold ||
                 a.y > b.y + b.h + threshold ||
                 a.y + a.h < b.y - threshold);
      },
      
      // Quick content hash to detect if canvas changed
      computeContentHash(imageData) {
        // Sample every 20th pixel for performance
        let hash = 0;
        const step = 20 * 4; // RGBA * 20 pixels
        for (let i = 0; i < imageData.data.length; i += step) {
          hash = ((hash << 5) - hash) + imageData.data[i];
          hash = ((hash << 5) - hash) + imageData.data[i + 1];
          hash = ((hash << 5) - hash) + imageData.data[i + 2];
        }
        return hash;
      },
      
      shouldDoFullRedraw() {
        if (this.fullRedraw) {
          this.fullRedraw = false;
          return true;
        }
        // Force full redraw every 60 frames as safety
        this.framesSinceChange++;
        if (this.framesSinceChange > 60) {
          this.framesSinceChange = 0;
          return true;
        }
        return false;
      },
      
      reset() {
        this.regions = [];
      }
    };

    // --- Three.js scene setup ---
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    // --- Fullscreen quad geometry ---
    const geometry = new THREE.PlaneGeometry(2, 2);
    
    // --- Enhanced CRT fragment shader with all canonical parameters ---
    const crtFragmentShader = `
      precision highp float;
      uniform float iTime;
      uniform vec3 iResolution;
      uniform sampler2D iChannel0;
      uniform vec2 uContentSize;
      // Texture size in texels (for bloom tap spacing)
      uniform vec2 uTexSize;
      // --- Visual effect controls ---
      uniform float uAmbient;
      uniform float uVignetteStrength;
      uniform float uShadowStrength;
      uniform vec3 uBezelColor;
      uniform vec3 uTableColor;
      // --- Screen/Phosphor controls ---
      uniform float uPhosphorR;
      uniform float uPhosphorG;
      uniform float uPhosphorB;
      uniform float uGamma;
      uniform float uNoiseAmount;
      uniform float uReflectionIntensity;
      uniform float uShine;
      uniform float uReflectRadius;
      // --- Geometry and glass controls ---
      uniform float uCurve;
      uniform float uWidth;
      uniform float uHeight;
      uniform float uBezelR;
      uniform float uSmooth;
      uniform float uGlassTint;
      uniform float uReflectionBlur;
      uniform float uBezelReflectionIntensity;
      // New – subtle scanline multiplier (0-1)
      uniform float uScanlineIntensity;
      // Content rows (buffer height) so scanlines are locked to the content grid
      uniform float uContentRows;
      // Animated grain on/off (0 under prefers-reduced-motion)
      uniform float uAnimateNoise;
      
      varying vec2 vUv;
      
      float rand(vec2 co){
          return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
      }
      
      vec2 CurvedSurface(vec2 uv, float r)
      {
          return r * uv/sqrt(r * r - dot(uv, uv));
      }
      
      vec2 crtCurve(vec2 uv, float r, bool content, bool shine)
      {
          r = uCurve * r;
          uv = (uv / iResolution.xy - 0.5) / vec2(iResolution.y/iResolution.x, 1.) * 2.0;
          uv = CurvedSurface(uv, r);
          if(content) uv *= 0.5 / uContentSize;
          uv = (uv / 2.0) + 0.5;
          return uv;
      }
      
      float roundSquare(vec2 p, vec2 b, float r)
      {
          return length(max(abs(p)-b,0.0))-r;
      }
      
      float stdRS(vec2 uv, float r)
      {
          return roundSquare(uv - 0.5, uContentSize + r, uBezelR);
      }
      
      vec2 borderReflect(vec2 p, float r)
      {
          float eps = 0.0001;
          vec2 epsx = vec2(eps,0.0);
          vec2 epsy = vec2(0.0,eps);
          vec2 b = (1.+vec2(r,r))* 0.5;
          r /= 3.0;
          p -= 0.5;
          vec2 normal = vec2(roundSquare(p-epsx,b,r)-roundSquare(p+epsx,b,r),
                             roundSquare(p-epsy,b,r)-roundSquare(p+epsy,b,r))/eps;
          float d = roundSquare(p, b, r);
          p += 0.5;
          return p + d*normal;
      }
      


      
      void main() {
        vec2 fragCoord = vUv * iResolution.xy;
        float grad = smoothstep(0.8, 1.0, length((fragCoord - 0.5 * iResolution.xy) / (0.5 * iResolution.y)));
        vec3 bg = mix(vec3(0.0), vec3(0.0), grad); // absolute black
        vec4 c = vec4(bg, 1.0);
        vec2 uvC = crtCurve(fragCoord, 1., true, false);  // Content Layer
        vec2 uvS = crtCurve(fragCoord, 1., false, false); // Screen Layer
        vec2 uvE = crtCurve(fragCoord, 1.25, false, false); // Enclosure Layer
        float ambient = uAmbient;
        
        // Ambient
        c += max(0.0, ambient - 0.3*distance(uvS, vec2(0.5,0.5))) *
            smoothstep(uSmooth, -uSmooth, stdRS(uvS, 0.0));
        // Inner Border
        c += vec4(uBezelColor, 0.0) * ambient * 0.7 *
            smoothstep(-uSmooth, uSmooth, stdRS(uvS, 0.0)) *
            smoothstep(uSmooth, -uSmooth, stdRS(uvE, uBezelR));
        // Corner
        c -= (vec4(uBezelColor, 0.0) )*
            smoothstep(-uSmooth*2.0, uSmooth*10.0, stdRS(uvE, uBezelR)) *
            smoothstep(uSmooth*2.0, -uSmooth*2.0, stdRS(uvE, uBezelR));
        // Outer Border
        c += vec4(uBezelColor, 0.0) * ambient *
            smoothstep(-uSmooth, uSmooth, stdRS(uvE, uBezelR)) *
            smoothstep(uSmooth, -uSmooth, stdRS(uvE, 0.15));
        // Inner Border Reflection
        for(int i = 0; i < 5; i++)
        {
            vec2 uvR = borderReflect(uvC + (vec2(rand(uvC+float(i)), rand(uvC+float(i)+0.1))-0.5)*uReflectionBlur, uReflectRadius);
            c += (vec4(0.2, 1.0, 0.2, 0.) - vec4(uBezelColor, 0.0)*ambient) * textureLod(iChannel0, uvR, 0.0) * uBezelReflectionIntensity *
                smoothstep(-uSmooth, uSmooth, stdRS(uvS, 0.0)) *
                smoothstep(uSmooth, -uSmooth, stdRS(uvE, uBezelR));
        }
        // Bloom: sharp pass (keeps the original exposure) plus a soft halo built from
        // mip levels of the screen texture (generateMipmaps=true). Tap offsets are
        // scaled by the LOD footprint so the halo is smooth, and its weight is low so
        // glyph edges stay crisp.
        vec2 bloomPx = 1.0 / uTexSize;
        vec4 bloomSharp = textureLod(iChannel0, uvC, 0.0);
        vec4 bloomSoft = (textureLod(iChannel0, uvC, 2.0) * 0.40 +
                          textureLod(iChannel0, uvC + vec2( 1.5, 1.5) * bloomPx * 4.0, 3.0) * 0.15 +
                          textureLod(iChannel0, uvC + vec2(-1.5, 1.5) * bloomPx * 4.0, 3.0) * 0.15 +
                          textureLod(iChannel0, uvC + vec2( 1.5,-1.5) * bloomPx * 4.0, 3.0) * 0.15 +
                          textureLod(iChannel0, uvC + vec2(-1.5,-1.5) * bloomPx * 4.0, 3.0) * 0.15);
        c += (bloomSharp * 1.2 + bloomSoft * 1.0)
            * smoothstep(0., -uSmooth*20., stdRS(uvS, -0.02)) * 0.5;
        // Table and room (parameterized)
        float vignette = smoothstep(0.0, uVignetteStrength, length((fragCoord - 0.5 * iResolution.xy) / iResolution.y));
        float tableShadow = max(0. , (1. - 2.0* fragCoord.y/iResolution.y)) *
            smoothstep(-0.25, 0.25, roundSquare(uvC - vec2(0.5, -0.2), uContentSize + vec2(0.25, -0.15), .1)) *
            smoothstep(-uSmooth*2.0, uSmooth*2.0, roundSquare(uvE-vec2(0.5, 0.5), uContentSize + 0.15, uBezelR));
        c += mix(vec4(0.08, 0.07, 0.06, 1.0), vec4(uTableColor, 0.), tableShadow * uShadowStrength) * (1.0 - vignette);
        
        // Derivative-based footprint must be computed outside divergent control flow.
        // rowsPerPx = how many content rows fall in one output pixel (>=1 -> aliasing).
        float rowsPerPx = fwidth(uvC.y * uContentRows);

        // --- Screen content with noise (ASCII art via sprites) ---
        if (uvC.x > 0. && uvC.x < 1. && uvC.y > 0. && uvC.y < 1.) {
          vec4 screen = textureLod(iChannel0, uvC, 0.0);
          
          // Phosphor response: normalise the RGB phosphor to its strongest channel and
          // apply it as a soft tint so the screen colour follows PHOSPHOR_R/G/B.
          vec3 phosphor = vec3(uPhosphorR, uPhosphorG, uPhosphorB);
          vec3 phosphorTint = phosphor / max(max(phosphor.r, phosphor.g), max(phosphor.b, 0.001));
          screen.rgb *= mix(vec3(1.0), phosphorTint, 0.4);
          
          float n = (rand(uvC * iResolution.xy + iTime * uAnimateNoise) - 0.5);
          screen.rgb += n * (uNoiseAmount * 0.3); // Reduced noise to prevent overwhelming content
          
          // Scanlines in CONTENT space: one dark band per content row (at row
          // boundaries), so they line up with the buffer rows at any output size.
          // When rows get denser than output pixels the contrast fades to avoid moire.
          float scanPhase = cos(6.28318531 * uvC.y * uContentRows);
          float scanAmp = mix(1.0, 0.3, smoothstep(0.55, 1.0, rowsPerPx));
          float scanline = scanPhase * scanAmp;
          float scanFactor = max(0.0, 1.0 - uScanlineIntensity * (0.3 + 0.3 * scanline));
          screen.rgb *= scanFactor;
          
          c.rgb += screen.rgb;
          c.a = max(c.a, screen.a);
          
          // Text rendering handled by canvas (no shader overlay needed)
        }
        
        // --- Glass tint effect ---
        vec4 glassTint = vec4(uGlassTint, uGlassTint, uGlassTint, 1.0);
        c = mix(c, glassTint, 0.05 * uGlassTint);
        
        // --- Reflection/shine effect ---
        vec2 uvSh = crtCurve(fragCoord, 1., false, true);
        float shine = max(0.0, uShine - distance(uvSh, vec2(0.5, 1.0))) * uReflectionIntensity;
        c.rgb += shine;
        
        // --- Gamma correction ---
        c.rgb = pow(max(c.rgb, vec3(0.0)), vec3(1.0 / uGamma));
        
        gl_FragColor = c;
      }
    `;
    // --- Create dedicated CRT canvas and texture ---
    let crtTexture;
    let crtCanvas = null;
    try {
      // Create a dedicated canvas for CRT rendering
      crtCanvas = document.createElement('canvas');
      crtCanvas.width = FRAME_W;
      crtCanvas.height = FRAME_H;

      const crtCtx = crtCanvas.getContext('2d', { willReadFrequently: true });
      if (!crtCtx) {
        console.error('[CRTShader] Failed to get 2D context for CRT canvas');
        throw new Error('Failed to get 2D context for CRT canvas');
      }

      // Copy content from DopeOS canvas if available AND if it has actual content
      if (dopeOSCanvasRef && dopeOSCanvasRef.current && dopeOSCanvasRef.current.getCanvas) {
        const dopeOSCanvas = dopeOSCanvasRef.current.getCanvas();
        if (dopeOSCanvas && dopeOSCanvas.width > 0 && dopeOSCanvas.height > 0) {
          try {
            const tempCtx = dopeOSCanvas.getContext('2d', { willReadFrequently: true });
            if (tempCtx) {
              // Quick check for non-black pixels in a small sample area
              const imageData = tempCtx.getImageData(0, 0, Math.min(50, dopeOSCanvas.width), Math.min(50, dopeOSCanvas.height));
              const hasContent = imageData.data.some((value, index) => {
                // Check RGB values (skip alpha channel) - any non-black pixel indicates content
                return index % 4 !== 3 && value > 5;
              });
              
              if (hasContent) {
                // Copy DopeOS content to CRT canvas only if it has real content
                crtCtx.drawImage(dopeOSCanvas, 0, 0, FRAME_W, FRAME_H);
                
                // Production debug: confirm initial transfer had content
                if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
                  console.log('[CRTShader]  Initial transfer - DopeOS canvas has content');
                }
              } else {
                // Production debug: skip empty canvas transfer
                if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
                  console.log('[CRTShader] ⏭️ Skipping initial transfer - DopeOS canvas is empty, will wait for animation loop');
                }
              }
            }
          } catch (err) {
            // If content check fails, just copy anyway (fallback)
            crtCtx.drawImage(dopeOSCanvas, 0, 0, FRAME_W, FRAME_H);
          }
        }
      }

      // Create texture from dedicated CRT canvas
      crtTexture = new THREE.Texture(crtCanvas);
      // Mipmapped so the bloom pass can sample blurred LODs (see fragment shader).
      // Sharp samples use textureLod(..., 0.0) so glyph crispness is unchanged.
      crtTexture.minFilter = THREE.LinearMipmapLinearFilter;
      crtTexture.magFilter = THREE.LinearFilter;
      crtTexture.generateMipmaps = true;
      crtTexture.needsUpdate = true;
    } catch (e) {
      console.error('[CRTShader] Error creating CRT canvas and texture:', e);
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event('dcw-crt-fallback')); } catch {/* ignore */}
      }
      return () => {};
    }

    // --- Compute half-sizes directly from phosphor bounds & frame aspect ---
    // uv space after crtCurve spans width = (FRAME_W/FRAME_H) and height = 1, then is scaled /2+0.5.
    // Rectangle half-width should therefore be (right-left)/2 * aspect, where aspect = FRAME_W/FRAME_H.
    const aspect = FRAME_W / FRAME_H;
    let contentHalfW, contentHalfH;
    // If portrait (aspect < 1), skip aspect multiplier to widen phosphor.
    const axisScale = aspect < 0.5 ? 0.5 : aspect;
    const contentScale = profileContentScale;
    contentHalfW = (PHOS.right - PHOS.left) * axisScale * 0.50 * contentScale;
    contentHalfH = (PHOS.bottom - PHOS.top) * 0.5 * contentScale;

    const contentVec = [contentHalfW, contentHalfH];
    
    // Reduced logging - dimensions logging is verbose

    const tweakMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tweak') === 'crt';
    const viewId      = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search).get('view') || 'default' : 'default';
    const storageKey  = `dcw_crt_${viewId}`;

    // Load any saved overrides
    if (tweakMode && typeof window !== 'undefined') {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
        Object.assign(paramsRef.current, saved);
      } catch { /* ignore */ }
    }

    const crtUniforms = {
      iTime: { value: 0 },
      iResolution: { value: new THREE.Vector3(FRAME_W, FRAME_H, 1) },
      iChannel0: { value: crtTexture },
      uContentSize: { value: new THREE.Vector2(contentVec[0], contentVec[1]) },
      // Visual effect controls
      uAmbient: { value: paramsRef.current.AMBIENT },
      uVignetteStrength: { value: paramsRef.current.VIGNETTE_STRENGTH },
      uShadowStrength: { value: paramsRef.current.SHADOW_STRENGTH },
      uBezelColor: { value: new THREE.Color(paramsRef.current.BEZEL_COLOR) },
      uTableColor: { value: new THREE.Color(paramsRef.current.TABLE_COLOR) },
      // Screen/Phosphor controls
      uPhosphorR: { value: paramsRef.current.PHOSPHOR_R },
      uPhosphorG: { value: paramsRef.current.PHOSPHOR_G },
      uPhosphorB: { value: paramsRef.current.PHOSPHOR_B },
      uGamma: { value: paramsRef.current.GAMMA },
      uNoiseAmount: { value: paramsRef.current.NOISE_AMOUNT },
      uReflectionIntensity: { value: paramsRef.current.REFLECTION_INTENSITY },
      uShine: { value: paramsRef.current.SHINE },
      uReflectRadius: { value: 0.05 },
      // Geometry and glass controls (now fully active)
      uCurve: { value: paramsRef.current.CURVE },
      uWidth: { value: paramsRef.current.WIDTH },
      uHeight: { value: paramsRef.current.HEIGHT },
      uBezelR: { value: paramsRef.current.BEZEL_R },
      uSmooth: { value: paramsRef.current.SMOOTH },
      uGlassTint: { value: paramsRef.current.GLASS_TINT },
      uReflectionBlur: { value: paramsRef.current.REFLECTION_BLUR },
      uBezelReflectionIntensity: { value: paramsRef.current.BEZEL_REFLECTION_INTENSITY },
      uScanlineIntensity: { value: paramsRef.current.SCANLINE_INTENSITY },
      uContentRows: { value: profileBufferH },
      uTexSize: { value: new THREE.Vector2(FRAME_W, FRAME_H) },
      uAnimateNoise: { value: prefersReducedMotion() ? 0 : 1 },
    };
    uniformsRef.current = crtUniforms;

    // -------------------------------------------
    //  🔧 Tweak panel (activated via ?tweak=crt)
    // -------------------------------------------
    if (tweakMode && typeof window !== 'undefined') {
      import('lil-gui').then(({ GUI }) => {
        if (window.__dcwCRTGui) return; // singleton
        const gui = new GUI({ title: 'CRT Tweaks' });
        window.__dcwCRTGui = gui;
        const affected = [
          'SCANLINE_INTENSITY', 'NOISE_AMOUNT', 'REFLECTION_BLUR',
          'GAMMA', 'GLASS_TINT'
        ];
        const uniformMap = {
          SCANLINE_INTENSITY: 'uScanlineIntensity',
          NOISE_AMOUNT: 'uNoiseAmount',
          REFLECTION_BLUR: 'uReflectionBlur',
          GAMMA: 'uGamma',
          GLASS_TINT: 'uGlassTint'
        };
        affected.forEach(key => {
          const range = key.includes('INTENSITY') ? [0, 2] : [0, 1.5];
          gui.add(paramsRef.current, key, range[0], range[1], 0.01).onChange(v => {
            const uni = uniformMap[key];
            if (uni && uniformsRef.current[uni]) uniformsRef.current[uni].value = v;
            try { localStorage.setItem(storageKey, JSON.stringify(paramsRef.current)); } catch { /* ignore */ }
          });
        });
      }).catch(() => console.warn('[CRT] lil-gui failed to load'));
    }
    
    let material;
    try {
      material = new THREE.ShaderMaterial({
        uniforms: crtUniforms,
        fragmentShader: crtFragmentShader,
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
          }
        `,
        depthWrite: false,
        depthTest: false,
      });
    } catch (e) {
      console.error('[CRTShader] Error creating shader material:', e);
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event('dcw-crt-fallback')); } catch {/* ignore */}
      }
      return () => {};
    }
    
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);
    // --- Orthographic camera for fullscreen quad ---
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // --- Renderer ---
    const captureMode = isCaptureMode();
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: false, // Disabled to prevent context conflicts
        powerPreference: 'default', // Changed to default for better compatibility
        failIfMajorPerformanceCaveat: false,
        // Only keep the drawing buffer in capture/test mode (headless screenshots).
        // Players do not need it and it costs bandwidth on mobile GPUs.
        preserveDrawingBuffer: captureMode,
        // Force opaque framebuffer - alpha:false prevents compositor from clearing buffer
        alpha: false,
        // Disable premultiplied alpha for better color accuracy in screenshots
        premultipliedAlpha: false,
        precision: "highp",
        // Try WebGL 2 first, fallback to WebGL 1
        // forceWebGL1: true, // Removed to allow WebGL 2 when available
        pixelRatio: resolvePixelRatio(captureMode),
      });
      
      // Retina-crisp output for players (DPR capped at 2); 1 in capture mode.
      renderer.setPixelRatio(resolvePixelRatio(captureMode));

      // Hint for headless Chromium in CI to produce consistent screenshots
      // by ensuring the canvas retains its contents after presentation.
      try { 
        if (captureMode && renderer.getContext()?.attribs) {
          renderer.getContext().attribs.preserveDrawingBuffer = true;
          
          // Additional tuning for screenshot capture in headless environments
          if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Headless')) {
            // Force completion of pending commands
            renderer.getContext().flush();
            renderer.getContext().finish();
          }
        }
      } catch (e) {
        // Some WebGL implementations do not expose attribs; safe to ignore
      }
      
      // Check if the renderer has a valid context
      if (!renderer.getContext()) {
        throw new Error('WebGL context not available');
      }
      
      // Reduced logging - only log essential WebGL info in development
      if (process.env.NODE_ENV === 'development') {
        const gl = renderer.getContext();
        if (gl) {
          const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
          if (debugInfo) {
            const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
            console.log('[CRTShader] WebGL:', vendor, '- Max texture:', gl.getParameter(gl.MAX_TEXTURE_SIZE));
          }
        }
      }
    } catch (e) {
      console.error('[CRTShader] WebGLRenderer failed:', e?.message || e);
    }
    if (!renderer || !renderer.getContext()) {
      console.warn('[CRTShader] WebGL context unavailable – activating 2D fallback');
      if (typeof window !== 'undefined') {
        try { window.dispatchEvent(new Event('dcw-crt-fallback')); } catch {/* ignore */}
      }
      
      // SCREENSHOT FALLBACK: Create a 2D canvas that renders DopeOS content directly
      // This allows screenshot generation in headless environments without WebGL
      const fallbackCanvas = document.createElement('canvas');
      fallbackCanvas.width = FRAME_W;
      fallbackCanvas.height = FRAME_H;
      fallbackCanvas.style.cssText = `
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: ${FRAME_W}px;
        height: ${FRAME_H}px;
        background: #000;
        z-index: 10000;
      `;
      
      const container = mountRef.current;
      if (container) {
        container.appendChild(fallbackCanvas);
      }
      
      const fallbackCtx = fallbackCanvas.getContext('2d');
      let fallbackRunning = true;
      
      // Animation loop to render DopeOS canvas to fallback 2D canvas
      const fallbackRenderLoop = () => {
        if (!fallbackRunning) return;
        
        try {
          const dopeOSCanvas = dopeOSCanvasRef?.current?.getCanvas?.();
          if (dopeOSCanvas && fallbackCtx) {
            // Clear and draw DopeOS content
            fallbackCtx.fillStyle = '#000';
            fallbackCtx.fillRect(0, 0, FRAME_W, FRAME_H);
            
            // Scale DopeOS canvas to fit
            const scale = Math.min(FRAME_W / dopeOSCanvas.width, FRAME_H / dopeOSCanvas.height);
            const scaledW = dopeOSCanvas.width * scale;
            const scaledH = dopeOSCanvas.height * scale;
            const offsetX = (FRAME_W - scaledW) / 2;
            const offsetY = (FRAME_H - scaledH) / 2;
            
            fallbackCtx.drawImage(dopeOSCanvas, offsetX, offsetY, scaledW, scaledH);
            
            // Mark as ready for screenshot capture
            if (!window.DCW_CRT_READY) {
              window.DCW_CRT_READY = true;
              window.DCW_SCREENSHOT_READY = true;
              // Store data URL for screenshot script
              try {
                window.DCW_SCREENSHOT_PNG = fallbackCanvas.toDataURL('image/png');
              } catch (e) {
                console.warn('[CRTShader] Could not capture 2D fallback screenshot:', e);
              }
              console.log('[CRTShader] 2D fallback ready - screenshot capture available');
            }
          }
        } catch (e) {
          console.warn('[CRTShader] Fallback render error:', e);
        }
        
        requestAnimationFrame(fallbackRenderLoop);
      }
      
      fallbackRenderLoop();
      
      return () => {
        fallbackRunning = false;
        if (container && fallbackCanvas.parentNode === container) {
          container.removeChild(fallbackCanvas);
        }
      };
    }
    renderer.setClearColor(0x000000);
    // Enable auto clearing to ensure clean renders
    renderer.autoClear = true;

    // Force an initial clear to ensure we start with a clean buffer
    renderer.clear();

    function sizeRenderer() {
      let vw = window.innerWidth;
      let vh = window.innerHeight;

      // CRITICAL FIX: Android Keyboard Stability
      // If we are on mobile and height shrinks while width stays roughly the same,
      // it's likely the keyboard. We lock the CRT height to the stored "stable" height
      // to prevent the CRT from squashing/distorting.
      if (stableViewport.current.w === 0) {
        stableViewport.current = { w: vw, h: vh };
      }
      
      // Check if this is a mobile device or small screen
      const isMobile = typeof navigator !== 'undefined' && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || vw < 800);
      
      // Determine if this is a legitimate resize (orientation change) or keyboard
      const widthChanged = Math.abs(vw - stableViewport.current.w) > 50; 
      const heightGrew = vh > stableViewport.current.h;
      
      if (widthChanged || heightGrew) {
         stableViewport.current = { w: vw, h: vh };
      } else if (isMobile && vh < stableViewport.current.h) {
         // Keyboard detected - override vh to prevent squashing
         // We verify it's not a tiny iframe resize by checking if the shrink is significant (>15%)
         // which is typical for keyboards (usually 30-40%)
         const shrinkRatio = 1 - (vh / stableViewport.current.h);
         if (shrinkRatio > 0.15) {
             console.log('[CRTShader] ⌨️ Keyboard detected - Locking CRT height to', stableViewport.current.h, '(current:', vh, ')');
             vh = stableViewport.current.h;
         } else {
             // Small resize, likely browser chrome adjustment, allow it
             stableViewport.current.h = vh;
         }
      }

      // CRITICAL FIX: Enhanced iframe viewport detection for Farcaster/Base/Mobile
      const isSmallViewport = vw < 100 || vh < 100;
      
      if (isSmallViewport || isInIframe) {
        // Try multiple viewport detection methods for iframe contexts
        const de = document.documentElement;
        const body = document.body;
        
        // Method 1: Document element dimensions
        const deVw = de.clientWidth || de.scrollWidth || de.offsetWidth;
        const deVh = de.clientHeight || de.scrollHeight || de.offsetHeight;
        
        // Method 2: Body dimensions (for some iframe contexts)
        const bodyVw = body.clientWidth || body.scrollWidth || body.offsetWidth;
        const bodyVh = body.clientHeight || body.scrollHeight || body.offsetHeight;
        
        // Method 3: Viewport meta and screen dimensions as iframe fallbacks
        const screenVw = screen.availWidth || screen.width || 960;
        const screenVh = screen.availHeight || screen.height || 720;
        
        // Method 4: Cached viewport from session
        let cachedVw = 960, cachedVh = 720;
        try {
          const prev = JSON.parse(sessionStorage.getItem('dcw_last_viewport') || '{}');
          cachedVw = prev.w || 960;
          cachedVh = prev.h || 720;
        } catch {/* ignore */}
        
        // Choose the largest reasonable viewport from all methods
        const candidates = [
          [vw, vh],
          [deVw, deVh], 
          [bodyVw, bodyVh],
          [cachedVw, cachedVh]
        ].filter(([w, h]) => w > 100 && h > 100 && w < 4000 && h < 4000);
        
        if (candidates.length > 0) {
          // Use the largest reasonable viewport (most likely to be correct)
          const [chosenVw, chosenVh] = candidates.reduce((largest, current) => 
            (current[0] * current[1]) > (largest[0] * largest[1]) ? current : largest
          );
          vw = chosenVw;
          vh = chosenVh;
        } else {
          // Last resort: Use screen dimensions for iframe contexts
          vw = Math.min(screenVw, 1024); // Cap at reasonable mobile size
          vh = Math.min(screenVh, 768);
        }
        
        // CRITICAL FIX: Always execute viewport debugging for external domains (Farcaster, Base, etc)
        console.log('[CRTShader] 🔧 VIEWPORT DETECTION:', {
          isInIframe,
          original: `${window.innerWidth}x${window.innerHeight}`,
          chosen: `${vw}x${vh}`,
          candidates: candidates.map(([w, h]) => `${w}x${h}`),
          documentElement: `${deVw}x${deVh}`,
          body: `${bodyVw}x${bodyVh}`,
          screen: `${screenVw}x${screenVh}`,
          cached: `${cachedVw}x${cachedVh}`,
          currentHostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
        });
      }
      const portrait = FRAME_H > FRAME_W;
      // Always contain the full CRT inside the viewport so that every
      // interactive element (close-buttons in the far corners, scrollbar, …)
      // remains visible and matchable to its hit-box.  Using `scaleH` in
      // portrait made the CRT wider than the device screen, pushing the
      // right-hand controls off-screen and causing the perceived mis-aligned
      // hit-boxes on mobile.  We therefore unify the logic: pick the smaller
      // of the two ratios regardless of orientation.

      const margin = 0;
      const scaleW = (vw * (1 - margin)) / FRAME_W;
      const scaleH = (vh * (1 - margin)) / FRAME_H;
      let scale;
      scale = Math.min(scaleW, scaleH);  // CONTAIN mode - preserve aspect ratio with bezel
      let targetW = Math.floor(FRAME_W * scale);
      let targetH = Math.floor(FRAME_H * scale);
      
      const isMobileProfile = FRAME_H > FRAME_W;
      
      // CRITICAL FIX: Ensure minimum viable size with mobile-aware minimums
      // For mobile profiles, use more appropriate minimums based on common mobile viewports
      let minSizeW, minSizeH;
      if (isMobileProfile && isInIframe) {
        // Mobile profile minimums - should fill most of a mobile viewport
        minSizeW = Math.min(vw * 0.8, 300); // 80% of viewport width or 300px max
        minSizeH = Math.min(vh * 0.8, 500); // 80% of viewport height or 500px max
      } else {
        // Desktop minimums
        minSizeW = 320;
        minSizeH = 240;
      }
      
      const minW = Math.max(targetW, minSizeW);
      const minH = Math.max(targetH, minSizeH);
      
      if (minW !== targetW || minH !== targetH) {
        console.log('[CRTShader] 📏 MINIMUM SIZE APPLIED:', {
          original: `${targetW}x${targetH}`,
          minimum: `${minW}x${minH}`,
          minimumType: isMobileProfile && isInIframe ? 'mobile-aware' : 'desktop'
        });
      }
      
      renderer.setPixelRatio(resolvePixelRatio(captureMode));
      renderer.setSize(minW, minH, false);
      renderer.domElement.style.width = `${minW}px`;
      renderer.domElement.style.height = `${minH}px`;
      renderer.domElement.style.imageRendering = 'pixelated';
      
      // PHANTOM CODE ELIMINATION: Revert complex iframe positioning that caused black screen
      // Use consistent transform-based centering for all contexts (desktop, mobile, iframe)
      // This ensures canvas is always visible and centered regardless of environment
      renderer.domElement.style.position = 'absolute';
      renderer.domElement.style.left = '50%';
      renderer.domElement.style.top = '50%';
      renderer.domElement.style.transform = 'translate(-50%, -50%)';
      renderer.domElement.style.zIndex = '10000';
      renderer.domElement.style.pointerEvents = 'auto';
      
      if (mountRef.current) {
        mountRef.current.style.width = `${minW}px`;
        mountRef.current.style.height = `${minH}px`;

        mountRef.current.style.left = '50%';
        mountRef.current.style.top = '50%';
        mountRef.current.style.transform = 'translate(-50%, -50%)';
        
        // Ensure mount container doesn't clip content
        mountRef.current.style.overflow = 'visible';
      }
      
      crtUniforms.iResolution.value.set(minW, minH, 1);
      
      // CRITICAL FIX: Always execute positioning debugging for external domains (Farcaster, Base, etc)
      // Calculate actual positioning for debug logging
      let leftOffset, topOffset;
      if (isInIframe) {
        leftOffset = Math.max(0, Math.floor((vw - minW) / 2));
        topOffset = isMobileProfile ? 0 : Math.max(0, Math.floor((vh - minH) / 2)); // Mobile profiles: no vertical offset
      } else {
        leftOffset = 'transform-center';
        topOffset = 'transform-center';
      }
      
      console.log('[CRTShader] 🎯 SIZING APPLIED:', {
        viewport: `${vw}x${vh}`,
        scale: scale.toFixed(3),
        originalTarget: `${targetW}x${targetH}`,
        appliedSize: `${minW}x${minH}`,
        frameSize: `${FRAME_W}x${FRAME_H}`,
        isMobileProfile,
        positioning: isInIframe ? (isMobileProfile ? `mobile-fullheight: ${leftOffset},${topOffset}` : `desktop-centered: ${leftOffset},${topOffset}`) : 'desktop-transform',
        expectedCenter: isInIframe ? `${leftOffset + minW/2},${topOffset + minH/2}` : 'viewport-center',
        currentHostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown'
      });
    }

    // Lock viewport to prevent page scrolling / bounce on mobile
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPos = document.body.style.position;
    const prevBodyWidth = document.body.style.width;
    const prevBodyHeight = document.body.style.height;
    const prevOverscroll = document.body.style.overscrollBehavior;

    // Allow scroll on document element to enable chat scrolling
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.overscrollBehavior = 'none';

    // Allow scroll within chat containers specifically
    const allowChatScroll = (e) => {
      // Check if the scroll event is within a chat container
      let target = e.target;
      let isChatScrollable = false;

      // Walk up the DOM tree to find chat containers
      while (target && target !== document.body) {
        if (target.classList && (
          target.classList.contains('dcw-chat-list') ||
          target.classList.contains('chat-container') ||
          target.getAttribute('data-chat-scrollable') === 'true'
        )) {
          isChatScrollable = true;
          break;
        }
        target = target.parentElement;
      }

      // If this is a chat container, allow the scroll
      if (isChatScrollable) {
        // Allow natural scroll behavior for chat containers
        return; // Do not prevent the event
      }

      // Prevent scroll for everything else
      e.preventDefault();
      e.stopPropagation();
    };

    // Remove scroll prevention - allow natural scroll behavior
    // document.body.addEventListener('wheel', allowChatScroll, { passive: false });
    // document.body.addEventListener('touchmove', allowChatScroll, { passive: false });

    sizeRenderer();
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    renderer.domElement.style.position = 'absolute';

    // Forward wheel events to DopeOS via inputManager for proper scroll handling
    renderer.domElement.addEventListener('wheel', (e) => {
      const [bx, by] = crtCanvasToBufferXYFromEvent(e);
      if (bx !== -1 && by !== -1) {
        // Use dynamic import to avoid circular dependency
        import('../../../engine/inputManager').then(({ handleWheel }) => {
          const consumed = handleWheel(e, bx, by);
          if (consumed) e.preventDefault();
        }).catch(() => {
          // Fallback: forward to DopeOS canvas directly
          const dopeosCanvas = window.DCW_getDopeOSCanvas?.();
          if (dopeosCanvas) {
            const newEvent = new WheelEvent('wheel', {
              deltaX: e.deltaX,
              deltaY: e.deltaY,
              bubbles: true,
              cancelable: true
            });
            dopeosCanvas.dispatchEvent(newEvent);
          }
        });
      }
      e.preventDefault();
    }, { passive: false });

    mountRef.current.appendChild(renderer.domElement);

    // CRITICAL FIX: Size renderer immediately after mounting to prevent initial invisibility
    sizeRenderer();
    
    // CRITICAL FIX: Always confirm renderer mounting for external domains (Farcaster, Base, etc)
    console.log('[CRTShader]  Renderer mounted and sized immediately');
    console.log('[CRTShader] Current hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
    const rect = renderer.domElement.getBoundingClientRect();
    console.log(`[CRTShader] Initial renderer size: ${rect.width}x${rect.height}`);

    // ─── Context loss recovery (Base Wallet WebView) ───────────────────────
    const handleContextLost = (e) => {
      // Prevent default so the browser doesn’t display an error dialog.
      e.preventDefault();
      // Only apply special handling when inside Base miniKit runtime.
      if (typeof window !== 'undefined') {
        try {
          const miniKit = window.miniKit;
          if (miniKit) {
            // Soft reload reinitialises React + WebGL without clearing cache.
            window.location.reload();
          }
        } catch (error) {
          // Silently ignore cross-origin access errors
          if (!(error instanceof DOMException && error.name === 'SecurityError')) {
            console.warn('[CRTShader] Error accessing miniKit:', error);
          }
          // Fallback – hard reload.
          window.location.reload();
        }
      }
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost, false);

    window.addEventListener('resize', sizeRenderer);

    // ─── miniKit foreground handler (Base App) ───────────────────────────
    let offForeground;
    if (typeof window !== 'undefined') {
      try {
        const miniKit = window.miniKit;
        if (miniKit?.onForeground) {
          const restore = () => {
            try {
              const gl = renderer.getContext();
              if (gl?.isContextLost?.()) {
                renderer.forceContextRestore?.();
                // If still lost fallback reload
                if (gl.isContextLost()) {
                  window.location.reload();
                }
              }
            } catch {/* ignore */}
          };
          offForeground = miniKit.onForeground(restore);
        }
      } catch (error) {
        // Silently ignore cross-origin access errors
        if (!(error instanceof DOMException && error.name === 'SecurityError')) {
          console.warn('[CRTShader] Error accessing miniKit foreground:', error);
        }
      }
    }

    // --- Canonical pointer mapping (full CRT buffer) ---
    function crtCanvasToBufferXYFromEvent(e) {
      const rect = renderer.domElement.getBoundingClientRect();
      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }
      
      // Device-pixel aware mapping: convert client (CSS) coords → renderer
      // pixels, then undo the uniform scaling & letter-box padding applied in
      // sizeRenderer().  We purposefully map the *entire* CRT buffer (bezel
      // and all) so canonical UI like the close-button drawn at y = 0 remains
      // clickable even on tall aspect ratios where the phosphor region starts
      // lower down the glass.

      // Compute pixel coordinates inside the CRT canvas (device-pixel aware)
      const crtX = (clientX - rect.left) * (renderer.domElement.width / rect.width);
      const crtY = (clientY - rect.top)  * (renderer.domElement.height / rect.height);

      // Buffer resolution taken from the active profile
      const BUFFER_W = profileBufferW;
      const BUFFER_H = profileBufferH;

      // ------------------------------------------------------------------
      //  Barrel-distortion aware mapping (matches the CRT shader exactly)
      // ------------------------------------------------------------------
      // Replicates the maths inside crtCurve() + CurvedSurface() from the
      // fragment shader so that the pointer → buffer translation accounts
      // for the radial displacement applied on-screen.  This eliminates the
      // 5-10 px discrepancy observed near the edges on mobile.

      // Compute half-size of phosphor region in "crtCurve" space (same calc
      // used when building uContentSize uniform).
      const aspect      = FRAME_W / FRAME_H;
      const axisScale   = aspect < 0.5 ? 0.5 : aspect;
      const contentScale = profileContentScale;
      const contentHalfW = (PHOS.right - PHOS.left) * axisScale * 0.50 * contentScale;
      const contentHalfH = (PHOS.bottom - PHOS.top) * 0.5 * contentScale;

      // Step 1 – map canvas pixel → normalised uv space (−1…1).
      let u = ((crtX / renderer.domElement.width)  - 0.5) / (renderer.domElement.height / renderer.domElement.width) * 2.0;
      let v = ((crtY / renderer.domElement.height) - 0.5) * 2.0;

      // Step 2 – apply the same barrel formula as CurvedSurface().
      const rCurve = (paramsRef.current?.CURVE ?? 3.0);
      const dotUV  = u * u + v * v;
      const denom  = Math.sqrt(Math.max(rCurve * rCurve - dotUV, 1e-6));
      if (!isFinite(denom) || denom === 0) return [-1, -1];
      u = (rCurve * u) / denom;
      v = (rCurve * v) / denom;

      // Step 3 – scale to phosphor bounds (matches `uv *= 0.5 / uContentSize`).
      u *= 0.5 / contentHalfW;
      v *= 0.5 / contentHalfH;

      // Step 4 – back to 0-1 texture space (`uv = uv/2 + 0.5`).
      u = u / 2 + 0.5;
      v = v / 2 + 0.5;

      // Convert to buffer pixels.
      const bx = u * BUFFER_W;
      const by = v * BUFFER_H;

      if (bx < 0 || bx > BUFFER_W || by < 0 || by > BUFFER_H) return [-1, -1];
      return [bx, by];
    }
    
    // ---------------------------------------------------------------------
    // CRITICAL FIX: Forward CRT-mapped coordinates directly to DopeOS
    // The previous handleCRTInteraction bypassed crtCanvasToBufferXYFromEvent
    // and sent raw screen coordinates, causing the mismatch.
    // ---------------------------------------------------------------------
    const handleCRTInteraction = (e) => {
      // Enhanced event forwarding for iframe contexts where event delegation might fail
      e.preventDefault();
      e.stopPropagation();
      
      // Use the centralized mapping function that handles barrel distortion + scaling
      const [bx, by] = crtCanvasToBufferXYFromEvent(e);
      
      if (bx !== -1 && by !== -1 && dopeOSCanvasRef.current) {
          console.log('[CRTShader] Touch in CRT area - forwarding mapped coords to DopeOS:', { bx, by });
          // Pass the BUFFER coordinates directly
          dopeOSCanvasRef.current.handlePointerEvent(e, bx, by);
      } else {
          console.log('[CRTShader] Touch outside CRT area or invalid mapping - ignoring');
      }
    };
    
    // CRITICAL FIX: Add event listeners with passive:false for iOS
    // iOS requires passive:false to allow preventDefault() in touch events
    renderer.domElement.addEventListener('click', handleCRTInteraction);
    renderer.domElement.addEventListener('touchend', handleCRTInteraction, { passive: false });
    renderer.domElement.addEventListener('touchstart', handleCRTInteraction, { passive: false });
    
    // SCROLL SUPPORT: Handle touch move for scrolling via inputManager
    renderer.domElement.addEventListener('touchmove', (e) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      const [bx, by] = crtCanvasToBufferXYFromEvent({ 
        clientX: touch.clientX, 
        clientY: touch.clientY,
        touches: [{ clientX: touch.clientX, clientY: touch.clientY }]
      });
      if (bx !== -1 && by !== -1) {
        import('../../../engine/inputManager').then(({ handleTouchMove }) => {
          handleTouchMove(touch.clientX, touch.clientY, bx, by);
        }).catch(() => {});
      }
    }, { passive: false });
    
    // CRITICAL FIX: Add keyboard event listener to forward to DopeOS
    // This enables typing in the chat input on desktop
    const handleKeyboardEvent = (e) => {
      if (dopeOSCanvasRef.current && dopeOSCanvasRef.current.handleKeyEvent) {
        dopeOSCanvasRef.current.handleKeyEvent(e);
      }
    };
    window.addEventListener('keydown', handleKeyboardEvent);

    window.dcwFarcasterBypassCleanup = () => {
      renderer.domElement.removeEventListener('click', handleCRTInteraction);
      renderer.domElement.removeEventListener('touchend', handleCRTInteraction, { passive: false });
      renderer.domElement.removeEventListener('touchstart', handleCRTInteraction, { passive: false });
      window.removeEventListener('keydown', handleKeyboardEvent);
      console.log('[CRTShader] ✅ Event listeners cleaned up');
    };
      
    // --- Animation loop: update uniforms and texture ---
    let frameId;
    
    // OPTIMIZATION: Uniform caching system to prevent redundant GPU state updates
    // Only updates uniforms when values actually change, reducing CPU->GPU bandwidth
    const uniformCache = {};
    const updateUniform = (name, value, setter) => {
      if (uniformCache[name] !== value) {
        setter(value);
        uniformCache[name] = value;
      }
    };
    
    // Special handling for color uniforms (objects that need .set())
    const colorCache = { uBezelColor: null, uTableColor: null };
    const updateColorUniform = (name, colorArray, uniform) => {
      const key = colorArray.join(',');
      if (colorCache[name] !== key) {
        uniform.set(colorArray);
        colorCache[name] = key;
      }
    };
    
    // -------------------------------------------------------------------
    //  Phosphor persistence (ADR-0025 item 5)
    // -------------------------------------------------------------------
    // Feedback pass: the screen texture is blended with the previous frame's
    // result (decayed) into a ping-pong render target, and the main CRT shader
    // samples that target instead of the raw canvas texture. 0 ms disables the
    // pass entirely (raw texture is sampled, zero extra cost).
    const PERSIST_KEY = 'dcw_phosphor_persistence_ms';
    const readPersistence = () => {
      try {
        const q = new URLSearchParams(window.location.search).get('persistence');
        if (q !== null && Number.isFinite(Number(q))) return Math.max(0, Number(q));
        const saved = localStorage.getItem(PERSIST_KEY);
        if (saved !== null && Number.isFinite(Number(saved))) return Math.max(0, Number(saved));
      } catch { /* ignore */ }
      // Capture/test mode stays deterministic: no temporal blending unless asked.
      return captureMode ? 0 : DEFAULT_PHOSPHOR_PERSISTENCE_MS;
    };
    let persistenceMs = readPersistence();
    let persistRT = null; // [A, B]
    let persistIdx = 0;
    let persistScene = null;
    let persistMaterial = null;
    let lastRenderTs = performance.now();
    const ensurePersistence = () => {
      if (persistRT) return true;
      try {
        const mk = () => new THREE.WebGLRenderTarget(FRAME_W, FRAME_H, {
          minFilter: THREE.LinearMipmapLinearFilter,
          magFilter: THREE.LinearFilter,
          generateMipmaps: true,
          depthBuffer: false,
          stencilBuffer: false,
        });
        persistRT = [mk(), mk()];
        persistMaterial = new THREE.ShaderMaterial({
          uniforms: {
            tCur: { value: crtTexture },
            tPrev: { value: persistRT[1].texture },
            uKeep: { value: 0 },
          },
          vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position, 1.0); }',
          fragmentShader: `
            precision highp float;
            uniform sampler2D tCur;
            uniform sampler2D tPrev;
            uniform float uKeep;
            varying vec2 vUv;
            void main() {
              vec4 cur = textureLod(tCur, vUv, 0.0);
              // Subtract ~1.5/255 so 8-bit quantisation can never pin a ghost at 1/255.
              vec4 prev = max(textureLod(tPrev, vUv, 0.0) * uKeep - vec4(1.5 / 255.0), vec4(0.0));
              gl_FragColor = vec4(max(cur.rgb, prev.rgb), 1.0);
            }`,
          depthWrite: false,
          depthTest: false,
        });
        persistScene = new THREE.Scene();
        persistScene.add(new THREE.Mesh(geometry, persistMaterial));
        return true;
      } catch (e) {
        console.warn('[CRTShader] Phosphor persistence unavailable:', e?.message || e);
        persistRT = null;
        persistenceMs = 0;
        return false;
      }
    };
    /** Render one frame: optional persistence feedback pass, then the CRT pass. */
    function renderFrame() {
      const now = performance.now();
      const dt = Math.min(0.25, Math.max(0, (now - lastRenderTs) / 1000));
      lastRenderTs = now;
      if (persistenceMs > 0 && ensurePersistence()) {
        const read = persistRT[persistIdx];
        const write = persistRT[1 - persistIdx];
        persistMaterial.uniforms.tCur.value = crtTexture;
        persistMaterial.uniforms.tPrev.value = read.texture;
        persistMaterial.uniforms.uKeep.value = persistenceDecay(persistenceMs, dt);
        renderer.setRenderTarget(write);
        renderer.render(persistScene, camera);
        renderer.setRenderTarget(null);
        persistIdx = 1 - persistIdx;
        crtUniforms.iChannel0.value = write.texture;
      } else if (crtUniforms.iChannel0.value !== crtTexture) {
        crtUniforms.iChannel0.value = crtTexture;
      }
      renderer.render(scene, camera);
    }
    /** Settings hook: ms until a lit phosphor fades to ~10%. 0 disables. */
    const setPhosphorPersistence = (ms) => {
      const v = Number(ms);
      persistenceMs = Number.isFinite(v) ? Math.max(0, v) : 0;
      try { localStorage.setItem(PERSIST_KEY, String(persistenceMs)); } catch { /* ignore */ }
    };
    if (typeof window !== 'undefined') {
      window.DCW_setPhosphorPersistence = setPhosphorPersistence;
      window.DCW_getPhosphorPersistence = () => persistenceMs;
    }

    // Reduced motion: freeze the grain (no animated noise) and stay in sync with the OS setting.
    let reducedMotionMql = null;
    const onReducedMotionChange = () => {
      crtUniforms.uAnimateNoise.value = prefersReducedMotion() ? 0 : 1;
    };
    try {
      reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      reducedMotionMql.addEventListener?.('change', onReducedMotionChange);
    } catch { /* ignore */ }

    let skipCounter = 0; // OPTIMIZATION: Frame skip counter for GPU-adaptive rendering
    
    function animate() {
      // OPTIMIZATION: Development mode logging helpers to reduce overhead in production
      const isDev = process.env.NODE_ENV === 'development' || 
                    (typeof window !== 'undefined' && 
                     (window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1'));
      const debugLog = (...args) => { if (isDev) console.log(...args); };
      const debugWarn = (...args) => { if (isDev) console.warn(...args); };
      
      // CRITICAL FIX: Robust boot frame counter for force-render timeout
      // Must be independent of DCW_CRT_READY or other flags
      if (typeof window !== 'undefined') {
        if (!window.DCW_BOOT_FRAME_COUNT) window.DCW_BOOT_FRAME_COUNT = 0;
        window.DCW_BOOT_FRAME_COUNT++;
      }

      // CRITICAL FIX: Always confirm animation loop for external domains (Farcaster, Base, etc)
      if (!window.DCW_CRT_ANIMATE_RUNNING_LOGGED) {
        debugLog('[CRTShader] 🎬 Animation loop running - ready to transfer DopeOS content');
        debugLog('[CRTShader] Current hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
        window.DCW_CRT_ANIMATE_RUNNING_LOGGED = true;
      }
      
      try {
        // CRITICAL: Disable ALL getImageData-based optimizations in web production
        // These "optimizations" cause MORE overhead than benefit in modern browsers:
        // - getImageData() is inherently expensive (even with willReadFrequently)
        // - Dirty rect calculations add CPU overhead
        // - Content sampling every frame causes stuttering
        // 
        // ONLY use simple full-redraw for smooth 60fps performance
        const isLocalhost = typeof window !== 'undefined' && 
                           (window.location.hostname === 'localhost' || 
                            window.location.hostname === '127.0.0.1');
        
        // DISABLE frame skipping entirely - always run at full 60fps
        const frameSkipConfig = { skip: 0, targetFps: 60 };
        
        crtUniforms.iTime.value = performance.now() / 1000;
        const p = paramsRef.current;
        
        // OPTIMIZATION: Batched uniform updates with caching
        // Only updates uniforms when values actually changed
        // Visual effect controls
        updateUniform('uAmbient', p.AMBIENT, v => crtUniforms.uAmbient.value = v);
        updateUniform('uVignetteStrength', p.VIGNETTE_STRENGTH, v => crtUniforms.uVignetteStrength.value = v);
        updateUniform('uShadowStrength', p.SHADOW_STRENGTH, v => crtUniforms.uShadowStrength.value = v);
        updateColorUniform('uBezelColor', p.BEZEL_COLOR, crtUniforms.uBezelColor.value);
        updateColorUniform('uTableColor', p.TABLE_COLOR, crtUniforms.uTableColor.value);
        
        // Screen/Phosphor controls
        updateUniform('uPhosphorR', p.PHOSPHOR_R, v => crtUniforms.uPhosphorR.value = v);
        updateUniform('uPhosphorG', p.PHOSPHOR_G, v => crtUniforms.uPhosphorG.value = v);
        updateUniform('uPhosphorB', p.PHOSPHOR_B, v => crtUniforms.uPhosphorB.value = v);
        updateUniform('uGamma', p.GAMMA, v => crtUniforms.uGamma.value = v);
        updateUniform('uNoiseAmount', p.NOISE_AMOUNT, v => crtUniforms.uNoiseAmount.value = v);
        updateUniform('uReflectionIntensity', p.REFLECTION_INTENSITY, v => crtUniforms.uReflectionIntensity.value = v);
        updateUniform('uShine', p.SHINE, v => crtUniforms.uShine.value = v);
        
        // Geometry and glass controls
        updateUniform('uCurve', p.CURVE, v => crtUniforms.uCurve.value = v);
        updateUniform('uWidth', p.WIDTH, v => crtUniforms.uWidth.value = v);
        updateUniform('uHeight', p.HEIGHT, v => crtUniforms.uHeight.value = v);
        updateUniform('uBezelR', p.BEZEL_R, v => crtUniforms.uBezelR.value = v);
        updateUniform('uSmooth', p.SMOOTH, v => crtUniforms.uSmooth.value = v);
        updateUniform('uGlassTint', p.GLASS_TINT, v => crtUniforms.uGlassTint.value = v);
        updateUniform('uReflectionBlur', p.REFLECTION_BLUR, v => crtUniforms.uReflectionBlur.value = v);
        updateUniform('uBezelReflectionIntensity', p.BEZEL_REFLECTION_INTENSITY, v => crtUniforms.uBezelReflectionIntensity.value = v);
        updateUniform('uScanlineIntensity', p.SCANLINE_INTENSITY, v => crtUniforms.uScanlineIntensity.value = v);
        
        // Update CRT canvas with latest DopeOS content
        let crtUpdateSuccess = false;
        
        // OPTIMIZATION: Use debugLog for transfer tracing to reduce production overhead
        if (!window.DCW_CRT_TRANSFER_ATTEMPT_LOGGED) {
          debugLog('[CRTShader]  Attempting DopeOS → CRT transfer...');
          debugLog(`[CRTShader] Components check: crtCanvas=${!!crtCanvas}, dopeOSCanvasRef=${!!dopeOSCanvasRef}, current=${!!dopeOSCanvasRef?.current}, getCanvas=${!!dopeOSCanvasRef?.current?.getCanvas}`);
          debugLog('[CRTShader] Current hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
          window.DCW_CRT_TRANSFER_ATTEMPT_LOGGED = true;
        }
        
        if (crtCanvas && dopeOSCanvasRef && dopeOSCanvasRef.current && dopeOSCanvasRef.current.getCanvas) {
          const dopeOSCanvas = dopeOSCanvasRef.current.getCanvas();
          
          // OPTIMIZATION: Use debugLog for canvas readiness tracing
          if (!window.DCW_DOPEOS_CANVAS_CHECK_LOGGED) {
            debugLog(`[CRTShader] DopeOS canvas check: exists=${!!dopeOSCanvas}, dimensions=${dopeOSCanvas?.width}x${dopeOSCanvas?.height}`);
            debugLog('[CRTShader] Canvas check hostname:', typeof window !== 'undefined' ? window.location.hostname : 'unknown');
            window.DCW_DOPEOS_CANVAS_CHECK_LOGGED = true;
          }
          
          if (dopeOSCanvas && dopeOSCanvas.width > 0 && dopeOSCanvas.height > 0) {
            // CRITICAL PERFORMANCE FIX: DISABLE content stability checks entirely
            // The getImageData() calls for sampling were causing severe performance degradation
            // in production (10-15fps, machine lag). Always assume content is stable and ready.
            // 
            // This eliminates:
            // - getImageData() overhead (even with willReadFrequently it's expensive)
            // - CPU-intensive pixel sampling loops
            // - Throttling complexity
            // 
            // Result: Simple full-redraw every frame at smooth 60fps
            const hasStableContent = true;
            
            if (hasStableContent) {
              const crtCtx = crtCanvas.getContext('2d', { willReadFrequently: true });
              if (crtCtx) {
                try {
                  // CRITICAL PERFORMANCE FIX: Removed debug getImageData() calls
                  // These were sampling pixels unnecessarily, adding overhead
                  
                  // CRITICAL PERFORMANCE FIX: Always do simple full redraw
                  // Dirty rectangle tracking was causing MORE overhead than benefit:
                  // - getImageData() for hash checking is expensive
                  // - Complex region calculation adds CPU overhead
                  // - Partial updates don't provide meaningful benefit in web browsers
                  // 
                  // Simple full redraw is fastest approach for smooth 60fps
                  crtCtx.clearRect(0, 0, FRAME_W, FRAME_H);
                  crtCtx.drawImage(dopeOSCanvas, 0, 0, FRAME_W, FRAME_H);

                  // Update texture
                  if (crtTexture) {
                    crtTexture.needsUpdate = true;
                  }
                  crtUpdateSuccess = true;
                  
                  // Texture update successful
                  // (Debug checking removed for performance - no getImageData() calls)
                  
                  // OPTIMIZATION: Use debugWarn for texture update errors
                } catch (err) {
                  if (!window.DCW_CRT_UPDATE_ERROR_LOGGED) {
                    debugWarn('[CRTShader] Failed to update CRT canvas from DopeOS:', err);
                    window.DCW_CRT_UPDATE_ERROR_LOGGED = true;
                  }
                }
              }
            } else {
              // FIXED: Removed periodic unstable frame logging (phantom code eliminated)
            }
          }
        }
        
        if (typeof window !== 'undefined') {
          if (!crtUpdateSuccess) {
            // Track consecutive failures for emergency reset trigger
            if (!window.DCW_CRT_FAIL_COUNT) window.DCW_CRT_FAIL_COUNT = 0;
            if (!window.DCW_CRT_FIRST_FAIL_TIME) window.DCW_CRT_FIRST_FAIL_TIME = Date.now();
            
            window.DCW_CRT_FAIL_COUNT++;
            
            // Production logging
            if (window.location.hostname === 'thedopechainwars.com' && !window.DCW_CRT_UPDATE_FAIL_LOGGED) {
              const hasAllComponents = crtCanvas && dopeOSCanvasRef && dopeOSCanvasRef.current && dopeOSCanvasRef.current.getCanvas;
              const dopeOSCanvas = dopeOSCanvasRef?.current?.getCanvas();
              const hasValidCanvas = dopeOSCanvas && dopeOSCanvas.width > 0 && dopeOSCanvas.height > 0;
              
              if (!hasAllComponents) {
                debugWarn('[CRTShader] CRT update failed - missing components:', {
                  crtCanvas: !!crtCanvas,
                  dopeOSCanvasRef: !!dopeOSCanvasRef,
                  current: !!dopeOSCanvasRef?.current,
                  getCanvas: !!dopeOSCanvasRef?.current?.getCanvas,
                  dopeOSCanvas: !!dopeOSCanvas
                });
              } else if (!hasValidCanvas) {
                debugWarn('[CRTShader] CRT update failed - invalid DopeOS canvas:', {
                  exists: !!dopeOSCanvas,
                  dimensions: dopeOSCanvas ? `${dopeOSCanvas.width}x${dopeOSCanvas.height}` : 'none'
                });
              } else {
                debugWarn('[CRTShader] CRT update failed - DopeOS content not yet stable (expected in early frames)');
              }
              window.DCW_CRT_UPDATE_FAIL_LOGGED = true;
            }
            
            // EMERGENCY RESET: If failed for too long, trigger emergency mobile detection reset
            const failDuration = Date.now() - window.DCW_CRT_FIRST_FAIL_TIME;
            const isLongFailure = failDuration > 8000; // 8 seconds
            const hasNumerousFailures = window.DCW_CRT_FAIL_COUNT > 60; // ~2 seconds at 30fps
            
            if ((isLongFailure || hasNumerousFailures) && !window.DCW_EMERGENCY_RESET_TRIGGERED) {
              debugWarn('[CRTShader]  Prolonged black screen detected - triggering emergency reset');
              window.DCW_EMERGENCY_RESET_TRIGGERED = true;
              
              // Import and call emergency reset
              import('../../../engine/device.js').then(({ emergencyMobileDetectionReset }) => {
                emergencyMobileDetectionReset();
              }).catch(e => {
                debugWarn('[CRTShader] Failed to import emergency reset:', e);
              });
            }
          } else {
            // Reset failure tracking on successful update
            if (window.DCW_CRT_FAIL_COUNT > 0) {
              debugLog('[CRTShader]  CRT update recovered after', window.DCW_CRT_FAIL_COUNT, 'failures');
            }
            window.DCW_CRT_FAIL_COUNT = 0;
            window.DCW_CRT_FIRST_FAIL_TIME = null;
            window.DCW_CRT_UPDATE_FAIL_LOGGED = false;
          }
        }
        
        // Set reflect radius based on orientation
        crtUniforms.uReflectRadius.value = FRAME_H > FRAME_W ? 0.05 : 0.05;
        
        // Check if renderer is still valid before rendering
        if (renderer && !renderer.getContext().isContextLost()) {
          // CRITICAL DEBUG: Check WebGL state before rendering
          // FIXED: Removed WebGL state debug logging (phantom code eliminated)
          
          renderFrame();
          
          // Production debug: verify renderer output is visible
          if (typeof window !== 'undefined' && window.location.hostname === 'thedopechainwars.com') {
            // Check once after initial renders if DOM element is properly attached and visible
            if (!window.DCW_RENDERER_VISIBILITY_CHECKED && renderer.domElement) {
              const rect = renderer.domElement.getBoundingClientRect();
              const isVisible = rect.width > 0 && rect.height > 0 && 
                               renderer.domElement.offsetParent !== null &&
                               getComputedStyle(renderer.domElement).visibility !== 'hidden' &&
                               getComputedStyle(renderer.domElement).opacity !== '0';
              
              debugLog('[CRTShader] 🔍 BLACK SCREEN DIAGNOSIS:', {
                isVisible,
                rect: `${rect.width}x${rect.height}`,
                offsetParent: !!renderer.domElement.offsetParent,
                visibility: getComputedStyle(renderer.domElement).visibility,
                opacity: getComputedStyle(renderer.domElement).opacity,
                display: getComputedStyle(renderer.domElement).display,
                zIndex: getComputedStyle(renderer.domElement).zIndex,
                position: getComputedStyle(renderer.domElement).position,
                parentElement: renderer.domElement.parentElement?.tagName,
                isInDocument: document.contains(renderer.domElement),
                webglContext: !!renderer.getContext(),
                contextLost: renderer.getContext()?.isContextLost(),
                textureValid: !!crtUniforms?.iChannel0?.value,
                textureImage: crtUniforms?.iChannel0?.value?.image ? 'present' : 'missing'
              });
              
              if (!isVisible) {
                debugWarn('[CRTShader] 🚨 Renderer element exists but not visually displayed:', {
                  rect: `${rect.width}x${rect.height}`,
                  offsetParent: !!renderer.domElement.offsetParent,
                  visibility: getComputedStyle(renderer.domElement).visibility,
                  opacity: getComputedStyle(renderer.domElement).opacity,
                  display: getComputedStyle(renderer.domElement).display
                });
              } else {
                debugLog('[CRTShader] Renderer element properly visible');
              }
              window.DCW_RENDERER_VISIBILITY_CHECKED = true;
            }
          }
        } else {
          debugWarn('[CRTShader] WebGL context lost during animation - attempting recovery');
          try {
            // Try to restore context
            renderer.forceContextRestore?.();
            if (!renderer.getContext().isContextLost()) {
              // Reduced logging - context restoration is normal
              renderFrame();
            } else {
              console.error('[CRTShader] Failed to restore context - activating fallback');
              if (typeof window !== 'undefined') {
                try { window.dispatchEvent(new Event('dcw-crt-fallback')); } catch {/* ignore */}
              }
              // Force reload as last resort
              if (typeof window !== 'undefined') {
                setTimeout(() => window.location.reload(), 1000);
              }
            }
          } catch (e) {
            console.error('[CRTShader] Error during context recovery:', e);
          }
        }

        // After the very first successful render, broadcast a readiness flag so
        // build-time screen-capturing scripts (generate_manifest_images.cjs) can
        // wait for the FULL CRT frame – bezel, curvature, reflections – before
        // taking the snapshot. This avoids the partially-rendered "zoomed" logo
        // that happened when only the raw DopeOS buffer was available.
        if (typeof window !== 'undefined' && !window.DCW_CRT_READY) {
          debugLog('[CRTShader] Marking CRT as ready - dispatching dcw-crt-ready event');
          window.DCW_CRT_READY = true;

          // Ensure DopeOS content is properly rendered before marking as ready
          let contentReady = false;
          if (crtCanvas && dopeOSCanvasRef && dopeOSCanvasRef.current && dopeOSCanvasRef.current.getCanvas) {
            const dopeOSCanvas = dopeOSCanvasRef.current.getCanvas();
            if (dopeOSCanvas && dopeOSCanvas.width > 0 && dopeOSCanvas.height > 0) {
              contentReady = true;
            }
          }
          
          debugLog(`[CRTShader] Content ready: ${contentReady}, dispatching ready event`);

          // Capture exact PNG of final framebuffer once
          try {
            if (!window.DCW_SCREENSHOT_PNG && renderer && renderer.getContext() && !renderer.getContext().isContextLost()) {
              // Force WebGL to finish all pending operations
              const gl = renderer.getContext();
              if (gl) {
                gl.flush();
                gl.finish();
              }

              // Ensure pixel ratio is set to 1 for consistent rendering
              const originalPixelRatio = renderer.getPixelRatio();
              renderer.setPixelRatio(1);

              // Force multiple render cycles to ensure complete frame is drawn
              renderFrame();
              renderFrame();
              renderFrame();

              // Capture screenshot with fallback content
              const canvas = renderer.domElement;

              // Ensure canvas is properly mounted and sized
              if (!canvas || !canvas.width || !canvas.height) {
                debugWarn('[CRTShader] Canvas not properly initialized for screenshot');
                // PHANTOM CODE ELIMINATED: Don't terminate animation loop, continue without screenshot
              } else {

              debugLog('[CRTShader] Screenshot capture - canvas dimensions:', canvas.width, 'x', canvas.height);

              // CRITICAL: Force WebGL to preserve the current frame
              // This is essential for screenshots to capture actual content
              if (gl) {
                // Read the pixel data directly from WebGL framebuffer
                const width = canvas.width;
                const height = canvas.height;
                const pixels = new Uint8Array(width * height * 4);
                
                // Force any pending operations to complete
                gl.flush();
                gl.finish();
                
                // Read pixels from the current framebuffer
                gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
                
                // Check if we actually have content
                const hasContent = pixels.some(pixel => pixel > 0);
                debugLog('[CRTShader] Canvas has WebGL content:', hasContent);
                
                // If we have WebGL content, create the screenshot from pixel data
                if (hasContent) {
                  const tempCanvas = document.createElement('canvas');
                  tempCanvas.width = width;
                  tempCanvas.height = height;
                  const tempCtx = tempCanvas.getContext('2d', { alpha: false });
                  
                  // Create ImageData from WebGL pixels (flip Y-axis as WebGL is bottom-up)
                  const imageData = tempCtx.createImageData(width, height);
                  for (let y = 0; y < height; y++) {
                    for (let x = 0; x < width; x++) {
                      const srcPos = (x + (height - y - 1) * width) * 4;
                      const destPos = (x + y * width) * 4;
                      imageData.data[destPos] = pixels[srcPos];     // R
                      imageData.data[destPos + 1] = pixels[srcPos + 1]; // G
                      imageData.data[destPos + 2] = pixels[srcPos + 2]; // B
                      imageData.data[destPos + 3] = 255; // Force fully opaque
                    }
                  }
                  
                  // Put the WebGL pixel data directly onto canvas
                  tempCtx.putImageData(imageData, 0, 0);
                  debugLog('[CRTShader] Screenshot captured from WebGL pixels');
                  
                  // Store the result - we got a good WebGL screenshot
                  window.DCW_SCREENSHOT_PNG = tempCanvas.toDataURL('image/png');
                  debugLog('[CRTShader] ✅ Screenshot successfully captured from WebGL pixels');
                  
                  // Restore original pixel ratio and skip fallback - we have real content
                  renderer.setPixelRatio(originalPixelRatio);
                  // IMPORTANT: Skip fallback block - we captured real WebGL content
                } else {
                  debugWarn('[CRTShader] No WebGL content detected, using fallback method');

                  // FALLBACK ONLY: Create canvas with preserveDrawingBuffer method + text overlay
                  // This block only runs when WebGL capture fails
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = canvas.width;
              tempCanvas.height = canvas.height;
              const tempCtx = tempCanvas.getContext('2d', { alpha: false });

              // Fill with black background
              tempCtx.fillStyle = '#000000';
              tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

              // Try to draw the WebGL canvas if preserveDrawingBuffer is working
              try {
                tempCtx.drawImage(canvas, 0, 0);
                debugLog('[CRTShader] Fallback: drew WebGL canvas to temp canvas');
              } catch (e) {
                debugWarn('[CRTShader] Could not draw WebGL canvas, using text fallback only:', e.message);
              }
              
              debugLog('[CRTShader] Screenshot captured with temp canvas dimensions:', tempCanvas.width, 'x', tempCanvas.height);

                  // Add CRT-style content as fallback
              tempCtx.fillStyle = '#39FF14';
              const fontSize = Math.max(16, Math.floor(canvas.height / 10));
              tempCtx.font = `bold ${fontSize}px monospace`;
              tempCtx.textAlign = 'center';
              tempCtx.textBaseline = 'middle';
              tempCtx.fillText('DOPE', canvas.width / 2, canvas.height * 0.4);
              tempCtx.fillText('CHAIN', canvas.width / 2, canvas.height * 0.5);
              tempCtx.fillText('WARS', canvas.width / 2, canvas.height * 0.6);

              // Add border for visibility
              tempCtx.strokeStyle = '#39FF14';
              tempCtx.lineWidth = Math.max(4, Math.floor(canvas.width / 100));
              tempCtx.strokeRect(
                tempCtx.lineWidth,
                tempCtx.lineWidth,
                canvas.width - tempCtx.lineWidth * 2,
                canvas.height - tempCtx.lineWidth * 2
              );

                  // Store the fallback result
              window.DCW_SCREENSHOT_PNG = tempCanvas.toDataURL('image/png');
                  debugLog('[CRTShader] ⚠️ Screenshot captured with fallback content');

              // Restore original pixel ratio
              renderer.setPixelRatio(originalPixelRatio);
            }
              } // Close if (gl)
            } // Close else block for canvas initialization check
          } // Close if (!window.DCW_SCREENSHOT_PNG...)
          } catch (e) {
            console.error('[CRTShader] Failed to capture PNG:', e);
          }

          // Flag for build scripts - set regardless of PNG capture success
          window.DCW_SCREENSHOT_READY = true;
          // Reduced logging - flag setting is normal
          
          // Fire event so outer app can hide placeholder & record metrics
          try { window.dispatchEvent(new Event('dcw-crt-ready')); } catch {/* ignore */}
          try { window.dispatchEvent(new Event('dcw-screenshot-ready')); } catch {/* ignore */}
          
          // Create a counter for frame stability check (for screenshots)
          window.DCW_FRAME_COUNT = 1;

          // Also save content of each canvas as data-url for screenshot tools
          // This creates a backup that can be used if direct screenshot fails
          setTimeout(() => {
            try {
              const canvases = Array.from(document.querySelectorAll('canvas'));
              window.DCW_CANVAS_DATAURL = canvases.map((canvas, i) => {
                try {
                  // For WebGL canvas, we need a special approach
                  // Skip WebGL processing to avoid context conflicts that break scrolling
                  const isWebGL = false; // Temporarily disabled to fix scrolling
                  if (isWebGL) {
                    // Force readPixels from WebGL context
                    const gl = isWebGL;
                    const width = canvas.width;
                    const height = canvas.height;
                    const pixels = new Uint8Array(width * height * 4);
                    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

                    // Create a new canvas to handle the pixel data
                    const pixelCanvas = document.createElement('canvas');
                    pixelCanvas.width = width;
                    pixelCanvas.height = height;
                    const pixelCtx = pixelCanvas.getContext('2d', { alpha: false });

                    // Fill with a background color first to ensure we have content
                    pixelCtx.fillStyle = '#000000';
                    pixelCtx.fillRect(0, 0, width, height);

                    // Create ImageData and flip Y (WebGL is bottom-up)
                    const imageData = pixelCtx.createImageData(width, height);
                    for (let y = 0; y < height; y++) {
                      for (let x = 0; x < width; x++) {
                        const srcPos = (x + (height - y - 1) * width) * 4;
                        const destPos = (x + y * width) * 4;
                        imageData.data[destPos] = pixels[srcPos]; // R
                        imageData.data[destPos+1] = pixels[srcPos+1]; // G
                        imageData.data[destPos+2] = pixels[srcPos+2]; // B
                        imageData.data[destPos+3] = 255; // Force fully opaque
                      }
                    }

                    // Put the pixel data on the canvas
                    pixelCtx.putImageData(imageData, 0, 0);

                    // Add a border to ensure visibility
                    pixelCtx.strokeStyle = '#39FF14';
                    pixelCtx.lineWidth = 4;
                    pixelCtx.strokeRect(2, 2, width - 4, height - 4);

                    return pixelCanvas.toDataURL('image/png');
                  } else {
                    // Regular 2D canvas
                    return canvas.toDataURL('image/png');
                  }
                } catch (e) {
                  console.error(`Error capturing canvas ${i}:`, e.message);
                  return null;
                }
              }).filter(Boolean);
            } catch (e) {
              console.error('[CRTShader] Error capturing canvas data URLs:', e);
            }
          }, 1000);
        } else if (typeof window !== 'undefined' && window.DCW_CRT_READY) {
          // Increment frame counter (helps screenshot tools ensure stability)
          if (typeof window.DCW_FRAME_COUNT === 'number') {
            window.DCW_FRAME_COUNT++;

            // After several stable frames, mark as highly reliable for screenshots
            if (window.DCW_FRAME_COUNT >= 5) {
              window.DCW_SCREENSHOT_STABLE = true;

              // Update canvas data URLs after we've reached stability
              if (!window.DCW_CANVAS_DATAURL_STABLE && window.DCW_FRAME_COUNT % 5 === 0) {
                try {
                  const canvases = Array.from(document.querySelectorAll('canvas'));
                  window.DCW_CANVAS_DATAURL_STABLE = canvases.map((canvas) => {
                    try {
                      return canvas.toDataURL('image/png');
                    } catch (e) {
                      return null;
                    }
                  }).filter(Boolean);
                } catch (e) {
                  /* Ignorar errores al capturar dataURL de canvas - puede fallar en WebGL seguro */
                }
              }
            }
          }
          
          // Re-capture screenshot if DCW_SCREENSHOT_PNG was cleared (for fresh capture requests)
          if (!window.DCW_SCREENSHOT_PNG && !window.DCW_SCREENSHOT_READY && window.DCW_FRAME_COUNT >= 3) {
            try {
              const gl = renderer.getContext();
              if (gl && !gl.isContextLost()) {
                gl.flush();
                gl.finish();
                
                const canvas = renderer.domElement;
                if (canvas && canvas.width > 0 && canvas.height > 0) {
                  const width = canvas.width;
                  const height = canvas.height;
                  const pixels = new Uint8Array(width * height * 4);
                  gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
                  
                  const hasContent = pixels.some(pixel => pixel > 0);
                  if (hasContent) {
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = width;
                    tempCanvas.height = height;
                    const tempCtx = tempCanvas.getContext('2d', { alpha: false });
                    
                    const imageData = tempCtx.createImageData(width, height);
                    for (let y = 0; y < height; y++) {
                      for (let x = 0; x < width; x++) {
                        const srcPos = (x + (height - y - 1) * width) * 4;
                        const destPos = (x + y * width) * 4;
                        imageData.data[destPos] = pixels[srcPos];
                        imageData.data[destPos + 1] = pixels[srcPos + 1];
                        imageData.data[destPos + 2] = pixels[srcPos + 2];
                        imageData.data[destPos + 3] = 255;
                      }
                    }
                    
                    tempCtx.putImageData(imageData, 0, 0);
                    window.DCW_SCREENSHOT_PNG = tempCanvas.toDataURL('image/png');
                    window.DCW_SCREENSHOT_READY = true;
                    debugLog('[CRTShader] ✅ Re-captured fresh screenshot on request');
                  }
                }
              }
            } catch (e) {
              debugWarn('[CRTShader] Re-capture failed:', e.message);
            }
          }
        }

        frameId = requestAnimationFrame(animate);
      } catch (err) {
        debugWarn('[CRTShader] Animation error:', err);
        // Try to recover by requesting next frame
        frameId = requestAnimationFrame(animate);
      }
    }

    // -------------------------------------------------------------------
    // Battery-save: pause the heavy RAF loop when tab is not visible
    // -------------------------------------------------------------------
    function handleVisibility() {
      // CRITICAL FIX: Farcaster Mobile on Android sometimes reports document.hidden = true initially
      // and doesn't reliably fire visibilitychange events when the webview comes to foreground.
      // We will IGNORE hidden state for the first 5 seconds to ensure the first frames render.
      const isEarlyBoot = (typeof window !== 'undefined') && 
                          (performance.now() < 5000 || (window.DCW_FRAME_COUNT || 0) < 30);

      if (document.hidden && !isEarlyBoot) {
        cancelAnimationFrame(frameId);
      } else {
        // Restart only if not already running
        cancelAnimationFrame(frameId); // Safety cancel to avoid duplicates
        frameId = requestAnimationFrame(animate);
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);

    // Mathematical text rendering removed (shader-based approach caused too many uniforms)
    
    // REVERT: Start animation immediately - prioritize visibility over perfect timing
    animate();
    
    // --- Cleanup ---
    return () => {
      // Cancel any pending animation frames
      cancelAnimationFrame(frameId);
      
      // FARCASTER BYPASS CLEANUP: Remove global event listeners
      if (typeof window !== 'undefined' && window.dcwFarcasterBypassCleanup) {
        window.dcwFarcasterBypassCleanup();
      }
      
      // Dispose of THREE.js resources
      if (material) {
        material.dispose();
      }
      
      if (geometry) {
        geometry.dispose();
      }
      
      try { reducedMotionMql?.removeEventListener?.('change', onReducedMotionChange); } catch { /* ignore */ }
      if (persistRT) persistRT.forEach((rt) => rt.dispose());
      if (persistMaterial) persistMaterial.dispose();
      if (typeof window !== 'undefined' && window.DCW_setPhosphorPersistence === setPhosphorPersistence) {
        delete window.DCW_setPhosphorPersistence;
        delete window.DCW_getPhosphorPersistence;
      }
      if (crtTexture) {
        crtTexture.dispose();
      }
      
      // Dispose of renderer and remove canvas
      if (renderer) {
        if (
          mountRef.current &&
          renderer.domElement &&
          renderer.domElement.parentNode === mountRef.current
        ) {
          mountRef.current.removeChild(renderer.domElement);
        }
        
        // Clear renderer internal resources
        renderer.dispose();
        renderer.forceContextLoss();
        
        if (renderer.domElement) {
          // Remove event listeners from canvas
          // NOTE: handlePointer is a local function inside useEffect and cannot be referenced here.
          // However, replacing the DOM element or its clone effectively removes listeners.
          // The old code tried to remove specific listeners but failed because handlePointer wasn't in scope.
          // Since we are disposing the renderer and removing the DOM element from mountRef,
          // explicit removal of listeners bound to internal closure functions is less critical
          // unless we are reusing the DOM element (which we aren't).
          // For safety against memory leaks if Three.js reuses the canvas:
          const clone = renderer.domElement.cloneNode(true);
          if (renderer.domElement.parentNode) {
            renderer.domElement.parentNode.replaceChild(clone, renderer.domElement);
          }
          // Remove global/window listeners that ARE in scope
          renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
        }
      }
      
      // Clean up scene
      if (scene) {
        scene.clear();
      }
      
      // Remove window/document event listeners
      window.removeEventListener('resize', sizeRenderer);
      document.removeEventListener('visibilitychange', handleVisibility);
      
      // Clean up miniKit foreground handler
      if (offForeground && typeof offForeground === 'function') {
        try {
          offForeground();
        } catch (e) {
          console.error('Error cleaning up miniKit foreground handler:', e);
        }
      }
      
      // Remove scroll event listeners
      // Remove scroll prevention - allow natural scroll behavior
      // document.body.removeEventListener('wheel', allowChatScroll);
      // document.body.removeEventListener('touchmove', allowChatScroll);

      // Restore previous document styles
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.height = 'auto';
      document.body.style.overflow = prevBodyOverflow || '';
      document.body.style.position = prevBodyPos || '';
      document.body.style.width = prevBodyWidth || '';
      document.body.style.height = prevBodyHeight || '';
      document.body.style.overscrollBehavior = prevOverscroll || '';
    };
  }, [dopeOSCanvasRef, profileId, profileBufferW, profileBufferH, profileContentScale]); // CRITICAL FIX: Use profile values, not object reference

  // Set body background to #000
  useEffect(() => {
    document.body.style.background = '#000';
    return () => { document.body.style.background = ''; };
  }, []);

  // --- Mount node only (no UI panel) ---
  return (
    <div
      ref={mountRef}
      style={{
        width: FRAME_W,
        height: FRAME_H,
        overflow: 'hidden',
        background: '#000',
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999, // CRITICAL FIX: Force highest z-index to prevent click blocking
      }}
    >
      {/* Fallback content that gets covered by canvas if successful */}
      <div 
        className="crt-fallback-text"
        style={{
        color: '#222', 
        fontFamily: 'monospace', 
        textAlign: 'center', 
        marginTop: '45%',
        fontSize: '12px',
        pointerEvents: 'none'
        }}
      >
        INITIALIZING VISUALS...
      </div>
    </div>
  );
};

export default CRTShader; 
